/**
 * Time Service
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getRecordedTime as getRecordedTimeAPI,
  getRecordedTimeForMatter as getRecordedTimeForMatterAPI,
  createTimeRecord as createTimeRecordAPI,
} from "api/index.js";

import { toast } from "react-toastify";

//----------------------------------------------------------------------------
// Helpers
//----------------------------------------------------------------------------
const buildOrderBy = (sort) => {
  if (!sort) return undefined;
  if (sort.dir === "desc") return `${sort.field} desc`;
  return sort.field;
};

//----------------------------------------------------------------------------
// Async thunks
//----------------------------------------------------------------------------
export const fetchRecordedTime = createAsyncThunk(
  "time/fetchRecordedTime",
  async (
    { feeEarner, status, fD, tD, matterId, page, limit, sort },
    { rejectWithValue }
  ) => {
    try {
      const orderBy = buildOrderBy(sort);
      const res = await getRecordedTimeAPI(
        feeEarner,
        status,
        fD,
        tD,
        matterId,
        page,
        limit,
        orderBy
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const fetchRecordedTimeForMatter = createAsyncThunk(
  "time/fetchRecordedTimeForMatter",
  async (
    { matterId, status, fD, tD, page, limit, sort },
    { rejectWithValue }
  ) => {
    try {
      const parameter = buildOrderBy(sort);
      const res = await getRecordedTimeForMatterAPI(
        matterId,
        status,
        fD,
        tD,
        page,
        limit,
        parameter
      );
      return { matterId, data: res.data };
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const addTimeRecord = createAsyncThunk(
  "time/addTimeRecord",
  async ({ datas, onSuccess }, { rejectWithValue }) => {
    try {
      const data = await createTimeRecordAPI(datas);
      toast.success("Time Target added Sucessfully");
      if (onSuccess) onSuccess();
      return data.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

//----------------------------------------------------------------------------
// Initial state
//----------------------------------------------------------------------------
const initialState = {
  recordedTime: [],
  MatterRecordedTime: {},
  timeTarget: [],
  isLoading: undefined,
  IsError: false,
  isSuccess: false,
  error: null,
};

//----------------------------------------------------------------------------
// Slice
//----------------------------------------------------------------------------
const timeSlice = createSlice({
  name: "time",
  initialState,
  reducers: {
    resetTimeError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // fetchRecordedTime
    builder
      .addCase(fetchRecordedTime.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchRecordedTime.fulfilled, (state, action) => {
        state.isLoading = false;
        state.recordedTime = action.payload;
        state.IsError = false;
        state.isSuccess = true;
      })
      .addCase(fetchRecordedTime.rejected, (state) => {
        state.isLoading = false;
        state.IsError = true;
      });

    // fetchRecordedTimeForMatter
    builder
      .addCase(fetchRecordedTimeForMatter.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchRecordedTimeForMatter.fulfilled, (state, action) => {
        state.isLoading = false;
        const { matterId, data } = action.payload;
        state.MatterRecordedTime = {
          ...state.MatterRecordedTime,
          [matterId]: data,
        };
        state.IsError = false;
        state.isSuccess = true;
      })
      .addCase(fetchRecordedTimeForMatter.rejected, (state) => {
        state.isLoading = false;
        state.IsError = true;
      });

    // addTimeRecord
    builder
      .addCase(addTimeRecord.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addTimeRecord.fulfilled, (state) => {
        state.isLoading = false;
        state.error = false;
        state.IsError = false;
        state.isSuccess = true;
      })
      .addCase(addTimeRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.IsError = true;
      });
  },
});

export const { resetTimeError } = timeSlice.actions;
export const timeReducer = timeSlice.reducer;
export default timeReducer;
